import _ from 'lodash';
import Bluebird from 'bluebird';
import SAlert from 'react-s-alert';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import { useHistory } from 'react-router';
import React, { Component, ReactNode, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import Table from 'corp/components/UI/Table';
import Select from 'corp/components/UI/Select';
import apiFetch from '@scale/frontend-lib/api-fetch';
import Multiselect from 'design-system/Multiselect';
import useQueryParams from '../hooks/useQueryParams';
import ObjectIdInput from 'design-system/ObjectIdInput';
import SelectProject from 'corp/components/UI/SelectProject';
import { SelectCustomerUncontrolled } from 'corp/components/UI/SelectCustomer';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import DatePicker from 'design-system/DatePicker';
import TimePicker from 'design-system/TimePicker';

import SelectTasksFull from 'corp/components/UI/SelectTasksFull';
import DropZone from 'react-scale/corp/components/DropZone';
import {
  DynamicCorpParams as AllDynamicCorpParams,
  EnabledDynamicCorpParams,
  AsyncDynamicCorpPageNames,
  SensitiveCorpPageNames,
  LongCorpJobPageNames,
} from '@scale/shared/corp/dynamicCorpToolManager';
import { Button, TextInput, Switch, Spinner, IconButton } from 'design-system';
import { H2, Text } from 'design-system/Typography';
import {
  DynamicCorpPage,
  IDynamicCorpParams,
  DynamicCorpInputTypes,
  evaluateShouldShow,
  evaluateSelectChoices,
  IDynamicCorpInput,
} from '@scale/shared/corp/constants';
import {
  invokeDynamicCorpJob,
  invokeCorpJob,
  invokeAsyncCorpJob,
} from '@scale/frontend-lib/invokeCorpJob';
import { useAsync } from 'react-use-v13';
import { useAllDynamicCorpPageTitles } from 'react-scale/corp/hooks/useAllDynamicCorpPageTitles';
import {
  requiredAsterisk as defaultAsterisk,
  DCT_DEPRECATION_BANNER_LIST,
  LayoutWithAlert,
} from 'react-scale/corp/helpers';
import AutoCompleteField from 'react-scale/corp/components/UI/AutoCompleteField/AutoCompleteField';
import DynamicChipList from 'react-scale/corp/components/UI/DynamicChipList/DynamicChipList';
import SelectProjectSLAGroup from 'corp/components/UI/SelectProjectSLAGroup';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import SmartLink from 'corp/components/SmartLink/SmartLink';
import { IProjectSLAGroup } from '@scale/shared/dailyPlan/dailyPlanProjectGroup';
import ProjectGroupDialog from 'react-scale/corp/views/DailyPlanning/ProjectGroups/ProjectGroupDialog';
import SelectBatch from 'react-scale/corp/components/UI/SelectBatch';
import { CorpJobStatus } from '@scale/shared/types/corpJobs/CorpJobStatus';
import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';
import { useQuery } from 'react-query';
import { WithPlaySound } from 'react-scale/corp/components/WithPlaySound';
import { soundProbability } from 'react-scale/corp/hooks/useIsPastReleaseDate';
import { QAOperationAssignmentForm } from 'react-scale/components/remoAdmin/qaOperation/QAOperationAssignmentForm';
import InputPresetWrapper from 'react-scale/corp/components/RuleEngine/InputPresetWrapper';
import { convertParameterToDynamicInputValue } from '@scale/shared/corp/ruleEngine';
import { DynamicSlaPicker } from 'react-scale/corp/components/QM/billing/slaDropdown';
import BillingFormWrapper from 'react-scale/corp/components/QM/billing/BillingFormWrapper';
import { FetchCorpJobProgress } from './CorpJobProgress';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import SelectContract from 'react-scale/corp/components/UI/SelectContract';
import { Timezone } from '@scale/shared/defaults';

import { ToolDefinitionByName } from '@scale/shared/toolbox/toolboxManager';
import { ToolName } from '@scale/shared/toolbox/toolName';
import { unleashFeatureFlagEnabled } from '@scale/frontend-lib/featureFlag';
import { GenAIOpsHubPaths } from './CorpProjectGroupManager/constants';

/*
  Use this file if you'd like to define your own function to render your corp tool output with
  If you want to use the default rendering, don't change anything, you're covered!
*/
type DynamicCorpOutputFn = (props: Record<string, any>) => JSX.Element;

const SUCCESS_MESSAGE =
  'Your job was successfully started. Please visit the job lookup page for more details (including whether it completed successfully)';

const TableRenderer: DynamicCorpOutputFn = props => {
  const headerRow = props.headers.map((header: any) => header.Header);
  const headerAccessors = props.headers.map((header: any) => header.accessor);
  const rowsToInput = [
    headerRow,
    ...props.rows.map((row: any) => headerAccessors.map((k: any) => row[k])),
  ];

  const filename = props.filename
    ? props.filename.endsWith('.csv')
      ? props.filename
      : props.filename + '.csv'
    : 'table_output.csv';
  return (
    <>
      {!!props.message && (
        <div style={{ marginBottom: '20px', whiteSpace: 'pre-wrap' }}>
          <Text>{props.message}</Text>
        </div>
      )}

      {!!props.corpJobOrSfnId && (
        <div style={{ marginBottom: '20px', whiteSpace: 'pre-wrap' }}>
          Job Lookup Page{' '}
          <Link href={`/lookup/${props.corpJobOrSfnId}`} target="_blank">
            here
          </Link>
        </div>
      )}

      <Table columns={props.headers} data={props.rows} />

      <div style={{ marginTop: '30px', textAlign: 'center' }}>
        <a
          href={'data:text/csv;charset=utf-8,' + rowsToInput.map(e => e.join(',')).join('\n')}
          download={filename}
          onClick={() => {
            SAlert.success('Downloading CSV!');
          }}
        >
          <FontAwesomeIcon icon="download" color="#586371" />

          <span>Export as CSV</span>
        </a>
      </div>
    </>
  );
};

const PayoutConfigValidationErrorRenderer: DynamicCorpOutputFn = props => {
  const headerRow = props.headers.map((header: any) => header.Header);
  const headerAccessors = props.headers.map((header: any) => header.accessor);
  const errorRowsToInput = props.errorRows.map((row: any) =>
    headerAccessors.map((k: any) => row[k]),
  );
  const warningRowsToInput = props.warningRows.map((row: any) =>
    headerAccessors.map((k: any) => row[k]),
  );
  const combinedInput = [
    [...headerRow, 'error/warn'],
    ...errorRowsToInput.map((row: any) => [...row, 'Error']),
    ...warningRowsToInput.map((row: any) => [...row, 'Warning']),
  ];
  return (
    <>
      {!!props.message && <div style={{ marginBottom: '20px' }}>{props.message}</div>}

      {props.errorRows.length > 0 && (
        <>
          {' '}
          <div style={{ color: 'red' }}>Please fix the following errors to proceed</div>
          <Table columns={props.headers} data={props.errorRows} />
        </>
      )}
      {props.warningRows.length > 0 && (
        <>
          {' '}
          Warnings
          <Table columns={props.headers} data={props.warningRows} />
        </>
      )}

      {props.errorRows.length + props.warningRows.length > 0 && (
        <div style={{ marginTop: '30px', textAlign: 'center' }}>
          <a
            href={'data:text/csv;charset=utf-8,' + combinedInput.map(e => e.join(',')).join('\n')}
            download="table_output.csv"
            onClick={() => {
              SAlert.success('Downloading CSV!');
            }}
          >
            <FontAwesomeIcon icon="download" color="#586371" />

            <span>Export as CSV</span>
          </a>
        </div>
      )}
    </>
  );
};

const JSONRenderer: DynamicCorpOutputFn = props => {
  const { json, url } = props;
  return (
    <>
      <div>
        <p>
          Temp url: <a href={url}>{url}</a>
        </p>
      </div>
      <ReactJsonView
        src={json}
        theme="monokai"
        enableClipboard={true}
        name={false}
        iconStyle="triangle"
      />
    </>
  );
};

const PromptOutputRenderer: DynamicCorpOutputFn = props => {
  const { json, outputPrompt, corpJobOrSfnId, longJob } = props;
  return (
    <>
      {corpJobOrSfnId && (
        <>
          <p>
            {longJob
              ? 'This corp tool takes a long time to run. See the status of your corp job'
              : 'Job Lookup Page'}{' '}
            <Link href={`/lookup/${corpJobOrSfnId}`} target="_blank">
              here
            </Link>{' '}
          </p>
        </>
      )}
      <div>
        <h3>Output Prompt:</h3>
        <p
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: '1.1rem',
            background: '#eee',
            borderRadius: 8,
            padding: 16,
            fontSize: 14,
          }}
        >
          {outputPrompt}
        </p>
      </div>
      <h3>Complete Output</h3>
      <ReactJsonView
        src={json}
        theme="monokai"
        enableClipboard={true}
        name={false}
        iconStyle="triangle"
      />
    </>
  );
};

const JSONsRenderer: DynamicCorpOutputFn = props => {
  const { messageJsons, prefix } = props;
  return (
    <>
      <h1>{prefix ?? ''}</h1>
      {messageJsons &&
        messageJsons.map((messageJson: { json: any; message: string }, i: number) => (
          <div key={i}>
            <h2>{messageJson.message}</h2>
            {messageJson.json && (
              <ReactJsonView
                src={messageJson.json}
                theme="monokai"
                enableClipboard={true}
                name={false}
                iconStyle="triangle"
              />
            )}
          </div>
        ))}
    </>
  );
};

const DynamicCorpOutputComponents: Readonly<Partial<Record<DynamicCorpPage, DynamicCorpOutputFn>>> =
  Object.freeze({
    [DynamicCorpPage.TestTable]: TableRenderer,
    [DynamicCorpPage.BatchUploadPayoutConfigs]: PayoutConfigValidationErrorRenderer,
    [DynamicCorpPage.GetHubstaffWorklogsCsv]: TableRenderer,
    [DynamicCorpPage.FedSc2sGetData]: TableRenderer,
    [DynamicCorpPage.FetchJsonBinaryHeader]: JSONRenderer,
    [DynamicCorpPage.EvaluateVideoWithPrompt]: PromptOutputRenderer,
    [DynamicCorpPage.GetPayoutStatus]: JSONsRenderer,
    [DynamicCorpPage.MassLinkAndRolloutTlHubstaffProjects]: TableRenderer,
    [DynamicCorpPage.MassLinkHubstaffProjects]: TableRenderer,
    [DynamicCorpPage.RolloutHubstaffProjects]: TableRenderer,
    [DynamicCorpPage.MarkInternalRedone]: TableRenderer,
  });

interface IDynamicCorpProps {
  isValid: boolean;
  hideInformationPanel?: boolean;
  displayProgress?: boolean;
  startButtonText?: string;
  presetFields?: Record<string, string>;
  presetOptions?: Record<string, string[]>;
  onInputUpdate?: (type: DynamicCorpInputTypes, key: string, value?: any) => void;
  onInputPresetUpdate?: (key: string, targetKey?: string) => void;
  documentTitle: string;
  pageLocation: string;
  pageName: DynamicCorpPage | undefined;
  params: IDynamicCorpParams | undefined;
  email: string;
  queryParams?: Record<string, any>;
  forceRenderingDisabledTool?: boolean;
  presetMetadata?: Record<string, any>;
}

interface IFormattedResponse {
  success?: boolean;
  pending?: boolean; // if the api timed out but code might still be running
  status?: CorpJobStatus;
  progress?: string;
  message: string;
  componentProps?: Record<string, any>;
  corpJobOrSfnId?: string;
  longJob?: boolean;
}

interface IDynamicCorpState {
  loading: boolean;
  // have inputs as a map so we can support multiple tools on one page
  inputs: Record<string, any>;
  metadata: Record<string, any>;
  latestResponse?: IFormattedResponse;
  showProjectGroupDialog?: boolean;
  lastRetrieveDate?: Date;
}

// Try to prevent users from rerunning executions if
// the API timed out but code is still running in the server
const TIMEOUT_ERROR_MESSAGE =
  `Your job may still be running, but it might take a while.\n` +
  `If you expect to receive a notification (email, Slack message, etc.) once it is completed, ` +
  `you can just wait a few minutes and look for that. \n` +
  `Otherwise, please check with the tool author on how to proceed.`;

const FETCH_JOB_STATUS_INTERVAL_MS = 10000;

/** see https://scale-api.quip.com/HMPSAl9Nap9O/Dynamic-Corp-Tooling-aka-Has-Corp */
export class DynamicCorp extends Component<IDynamicCorpProps, IDynamicCorpState> {
  constructor(props: IDynamicCorpProps) {
    super(props);

    this.state = this.defaultState;

    this.updateInput = this.updateInput.bind(this);
    this.getInput = this.getInput.bind(this);
    this.renderInput = this.renderInput.bind(this);
    this.submit = this.submit.bind(this);
    this.canSubmit = this.canSubmit.bind(this);
    this.renderOutput = this.renderOutput.bind(this);
  }

  get disallowCorpJob(): boolean {
    return (
      AsyncDynamicCorpPageNames.includes(this.props.pageName!) ||
      SensitiveCorpPageNames.includes(this.props.pageName!) ||
      LongCorpJobPageNames.includes(this.props.pageName!)
    );
  }

  getDefaultForObject(input: IDynamicCorpInput) {
    const val: any = {};
    (input.objectInputs ?? []).forEach((obInput: IDynamicCorpInput) => {
      val[obInput.bodyKey] = this.getDefaultForInput(obInput);
    });
    return val;
  }

  getDefaultForInput(input: IDynamicCorpInput) {
    const overriddenDefaultValue = this.props.queryParams?.[input.bodyKey];
    // Only support text for now
    if (
      !_.isNil(overriddenDefaultValue) &&
      ((input.allowQueryInput && input.type === DynamicCorpInputTypes.String) ||
        !!this.props.onInputUpdate)
    ) {
      return overriddenDefaultValue;
    } else if (typeof input.default === 'function') {
      return undefined;
    } else if (input.default) {
      return input.default;
    } else if (input.type === DynamicCorpInputTypes.Checkbox) {
      return false;
    } else if (input.type === DynamicCorpInputTypes.String) {
      return '';
    } else if (input.type === DynamicCorpInputTypes.Object && !this.props.onInputUpdate) {
      return this.getDefaultForObject(input);
    } else {
      return undefined;
    }
  }

  updateMetadataWithNewPresets(
    prevPresetMetadata: Record<string, any>,
    presetMetadata: Record<string, any>,
  ) {
    const newMetadata = _.cloneDeep(this.state.metadata);

    Object.keys(prevPresetMetadata).forEach(key => {
      delete newMetadata[key];
    });

    Object.keys(presetMetadata).forEach(key => {
      newMetadata[key] = presetMetadata[key];
    });

    this.setState(prevState => ({
      ...prevState,
      metadata: newMetadata,
    }));
  }

  get defaultState(): IDynamicCorpState {
    const inputs: IDynamicCorpState['inputs'] = {};
    const metadata: IDynamicCorpState['metadata'] = {};
    if (this.props.params) {
      this.props.params.inputs.forEach((input: IDynamicCorpInput) => {
        inputs[input.bodyKey] = this.getDefaultForInput(input);
      });
    }

    if (this.props.presetMetadata) {
      Object.keys(this.props.presetMetadata).forEach(key => {
        metadata[key] = this.props.presetMetadata![key];
      });
    }

    return {
      loading: false,
      inputs,
      metadata,
      latestResponse: undefined,
    };
  }

  componentDidUpdate(prevProps: IDynamicCorpProps) {
    if (!_.isEqual(this.props.presetMetadata, prevProps.presetMetadata)) {
      this.updateMetadataWithNewPresets(
        prevProps.presetMetadata ?? {},
        this.props.presetMetadata ?? {},
      );
    }
    if (this.props.pageName !== prevProps.pageName) {
      this.setState(this.defaultState);
    }
  }

  updateInput(
    inputKey: string,
    newValue: any,
    parentBodyKeys: string[],
    type: DynamicCorpInputTypes,
    metadataKey?: string,
  ) {
    const newInputs = _.cloneDeep(this.state.inputs);
    const nestedInputKey = [...parentBodyKeys, inputKey].join('.');
    const objectToCheckProp = parentBodyKeys.length
      ? _.get(newInputs, parentBodyKeys.join('.'))
      : newInputs;
    if (!(inputKey in objectToCheckProp)) {
      return;
    }
    _.set(newInputs, nestedInputKey, newValue);
    const newMetadata = _.cloneDeep(this.state.metadata ?? {});
    if (metadataKey) {
      _.set(newMetadata, metadataKey, newValue?.forMetadata);
      this.props.params?.inputs.forEach(input => {
        if (
          (_.isNil(input.updateWhenMetadataChanges) ||
            input.updateWhenMetadataChanges(newMetadata)) &&
          !_.isNil(input.default) &&
          typeof input.default === 'function'
        )
          newInputs[input.bodyKey] = input.default(this.state.inputs, newMetadata);
      });
    }
    if (this.props.onInputUpdate) {
      this.props.onInputUpdate(type, inputKey, newValue);
    }
    this.setState({
      metadata: newMetadata,
      inputs: newInputs,
    });
  }

  getInput(inputKey: string, parentBodyKeys: string[]) {
    return _.get(this.state.inputs, [...parentBodyKeys, inputKey].join('.'));
  }

  // eslint-disable-next-line complexity
  renderInput(
    input: IDynamicCorpParams['inputs'][number],
    parentBodyKeys: string[] = [],
    projectValue?: string,
  ) {
    const allValues = this.state.inputs;
    const shouldHide = !evaluateShouldShow(input.shouldShow, allValues, this.state.metadata);

    if (shouldHide) return null;
    const onChange = (newValue: any) => {
      this.updateInput(input.bodyKey, newValue, parentBodyKeys, input.type, input.metadataKey);
    };
    const currentValue = this.getInput(input.bodyKey, parentBodyKeys);
    let inputComponent: ReactNode = null;
    // Adding asterisk to checkbox implies you need to check the checkbox, which is not the case
    const requiredAsterisk =
      input.optional || input.type === DynamicCorpInputTypes.Checkbox ? null : defaultAsterisk;

    switch (input.type as DynamicCorpInputTypes) {
      case DynamicCorpInputTypes.Object:
        if (_.isNil(input.objectInputs) || _.isEmpty(input.objectInputs))
          throw new Error('You need to add objectInputs to an object input');
        inputComponent = (
          <div style={{ paddingLeft: '10px' }}>
            {input.objectInputs.map(i => {
              return _.isNil(_.get(currentValue, i.bodyKey))
                ? null
                : this.renderInput(i, [...parentBodyKeys, input.bodyKey]);
            })}
          </div>
        );
        break;
      case DynamicCorpInputTypes.String:
        inputComponent = (
          <TextInput
            key={input.bodyKey}
            onChange={e => onChange(e.target.value)}
            value={currentValue}
            fullWidth
          />
        );
        break;
      case DynamicCorpInputTypes.MultiLineString:
        inputComponent = (
          <TextInput
            key={input.bodyKey}
            onChange={e => onChange(e.target.value)}
            value={currentValue}
            multiline
            fullWidth
            rows={10}
          />
        );
        break;
      case DynamicCorpInputTypes.Number:
        // the NumericInput component sucks because it requires a default value
        // so just use ol' reliable
        inputComponent = (
          <TextInput
            type="number"
            key={input.bodyKey}
            onChange={e => onChange(parseFloat(e.target.value))}
            value={currentValue}
          />
        );
        break;

      case DynamicCorpInputTypes.DynamicChipList:
        inputComponent = (
          <DynamicChipList
            {...input.customInputProps}
            apiFetchEndpoint={input.customInputProps.apiFetchEndpoint(allValues)}
            key={input.bodyKey}
            value={currentValue}
            includeTestProjects={false}
            onChange={onChange}
            width="auto"
          />
        );
        break;

      case DynamicCorpInputTypes.AutoCompleteField:
        inputComponent = (
          <AutoCompleteField
            {...input.customInputProps}
            key={input.bodyKey}
            value={currentValue}
            includeTestProjects={false}
            onChange={onChange}
            width="auto"
          />
        );
        break;
      case DynamicCorpInputTypes.Project:
        inputComponent = (
          <SelectProject
            key={input.bodyKey}
            value={currentValue}
            includeTestProjects={false}
            onChange={onChange}
            width="auto"
          />
        );
        break;
      case DynamicCorpInputTypes.Customer:
        inputComponent = (
          <SelectCustomerUncontrolled
            onChange={(e: { value: string }) => onChange(e?.value)}
            excludeNonAdmins={input.customInputProps?.excludeNonAdmins}
          />
        );
        break;
      case DynamicCorpInputTypes.SingleObjectIdInput:
        inputComponent = (
          <ObjectIdInput
            key={input.bodyKey}
            initialInput={currentValue}
            onEntry={entries => onChange(_.first(entries))}
            singleObjectId={true}
            hideLabelPrompt={true}
          />
        );
        break;
      case DynamicCorpInputTypes.MultiObjectIdInput:
        inputComponent = (
          <ObjectIdInput
            initialInput={
              typeof currentValue === 'object' && Array.isArray(currentValue)
                ? currentValue.join(', ')
                : currentValue
            }
            onEntry={onChange}
            hideLabelPrompt={true}
          />
        );
        break;
      case DynamicCorpInputTypes.MultiEmailOrObjectIdInput:
        inputComponent = (
          <ObjectIdInput
            initialInput={
              typeof currentValue === 'object' && Array.isArray(currentValue)
                ? currentValue.join(', ')
                : currentValue
            }
            key={input.bodyKey}
            allowEmails={true}
            placeholder={'Object IDs or emails (comma or whitespace separated)'}
            onEntry={onChange}
            hideLabelPrompt={true}
          />
        );
        break;
      case DynamicCorpInputTypes.Checkbox:
        inputComponent = (
          <Switch
            key={input.bodyKey}
            checked={currentValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
          />
        );
        break;
      case DynamicCorpInputTypes.Select:
        inputComponent = (
          <Select
            key={input.bodyKey}
            options={evaluateSelectChoices(
              input.selectChoices,
              allValues,
              this.state.metadata,
            )!.map(value => ({
              value,
              label: value,
            }))}
            value={currentValue}
            onChange={(e: any) => onChange(e?.value)}
            clearable={input.optional === true}
          />
        );
        break;
      case DynamicCorpInputTypes.MultiSelect:
        inputComponent = (
          <Multiselect
            key={input.bodyKey}
            options={input.selectChoices!}
            selected={currentValue ?? []}
            handleChange={(e: string[]) => onChange(e)}
            position="bottom"
          />
        );
        break;
      case DynamicCorpInputTypes.SelectTasksFull:
        inputComponent = (
          <div className="select-tasks-full">
            <SelectTasksFull
              key={input.bodyKey}
              onChange={tasks => onChange(tasks.map(t => t._id))}
              filterSelectedTasks={[] as []}
            />
          </div>
        );
        break;
      case DynamicCorpInputTypes.Contract:
        inputComponent = (
          <SelectContract key={input.bodyKey} value={currentValue} onChange={onChange} />
        );
        break;
      case DynamicCorpInputTypes.ProjectSLAGroup:
        inputComponent = (
          <div className="select-project-SLA-group">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '5px' }}>
                <SelectProjectSLAGroup onChange={(e: { value: string }) => onChange(e.value)} />
                <div
                  style={{
                    padding: `8px`,
                    display: 'inline-block',
                    cursor: 'pointer',
                  }}
                >
                  <IconButton
                    size="medium"
                    data-tip={'Create Project SLA Group'}
                    onClick={() => {
                      this.setState({ showProjectGroupDialog: true });
                    }}
                  >
                    <FontAwesomeIcon icon={'plus-circle'} color={'#0076FF'} />
                  </IconButton>
                </div>
              </div>

              {currentValue && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold', paddingRight: '5px' }}>Selected:</span>{' '}
                  {_.isString(currentValue) ? (
                    <SmartLink type={INamedObjectTypes.ProjectSLAGroup} id={currentValue} />
                  ) : (
                    <>
                      new group
                      <span style={{ paddingLeft: '5px', fontWeight: 'bold' }}>
                        {currentValue.name}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
            {this.state.showProjectGroupDialog && (
              <ProjectGroupDialog
                onClose={() => {
                  this.setState({ showProjectGroupDialog: false });
                }}
                hideProjects={true}
                skipSave={true}
                returnData={(e?: IProjectSLAGroup) => e && onChange(e)}
              ></ProjectGroupDialog>
            )}
          </div>
        );
        break;
      case DynamicCorpInputTypes.Date:
        inputComponent = (
          <DatePicker
            key={input.bodyKey}
            onChange={onChange}
            selected={currentValue}
            placeholderText={
              input.optional ? moment(currentValue as Date).format('MM/DD/YYYY') : ''
            }
          />
        );
        break;
      case DynamicCorpInputTypes.Time:
        inputComponent = (
          <TimePicker
            key={input.bodyKey}
            selected={currentValue}
            onChange={onChange}
            timezone={Timezone.Local}
          />
        );
        break;
      case DynamicCorpInputTypes.SLAPicker:
        // This input requires knowing which customer and tasktype you are working with.
        // as such, it's pretty context-dependent, and currently works only with the
        // deep project clone tool for DCTs. You can use the underlying <SlaPicker /> anywhere you want.
        inputComponent = (
          <DynamicSlaPicker
            onChange={onChange}
            defaultSla={currentValue}
            key={input.bodyKey}
            sourceProject={this.state.inputs.sourceProjectId?.value}
            targetCustomer={this.state.inputs.targetCustomerId}
            targetProject={this.state.inputs.targetProjectId?.value}
            targetProjectStrategy={this.state.inputs.targetProjectStrategy}
          />
        );
        break;
      case DynamicCorpInputTypes.BillingForm:
        inputComponent = (
          <BillingFormWrapper
            onChange={onChange}
            currentState={currentValue}
            targetCustomerId={this.state.inputs.targetCustomerId}
            key={input.bodyKey}
          />
        );
        break;
      case DynamicCorpInputTypes.SingleFileAttachment:
        inputComponent = (
          <DropZone
            key={input.bodyKey}
            handleDrop={(e: File[]) => {
              const file = e[0];
              const maybeFileNameRegex: RegExp | undefined =
                input.customInputProps?.allowedFileNameRegex;
              if (maybeFileNameRegex && !maybeFileNameRegex.test(file.name)) {
                SAlert.error(`File name does not match the pattern ${maybeFileNameRegex}`);
                return;
              }
              onChange(file);
            }}
            file={currentValue}
          />
        );
        break;
      case DynamicCorpInputTypes.SelectBatch:
        inputComponent = (
          <SelectBatch
            key={input.bodyKey}
            onChange={(e: any) => onChange(e?.value)}
            value={currentValue}
            projectId={projectValue}
            useMaxWidth
          />
        );
        break;
      case DynamicCorpInputTypes.User:
        inputComponent = (
          <SelectScaler
            key={input.bodyKey}
            value={currentValue}
            onChange={(e: any) => onChange(e?.value)}
            placeholder="User"
          />
        );
        break;
      case DynamicCorpInputTypes.MultiUserOrTeamSelect:
        inputComponent = (
          <QAOperationAssignmentForm
            isCreateQAOperationFormField
            onChange={(e: any) => onChange(e)}
          />
        );
        break;
    }

    return (
      inputComponent && (
        <div
          className={input.type === DynamicCorpInputTypes.Object ? 'objectContainer' : 'container'}
          key={input.bodyKey}
        >
          <div className="textTitle">
            <Text medium>{input.label}</Text> {requiredAsterisk}
          </div>
          {input.help && <div className="textHelp">{input.help}</div>}
          <div className="inputComponent">
            <InputPresetWrapper
              presetOptions={
                input.type !== DynamicCorpInputTypes.MultiUserOrTeamSelect &&
                this.props.presetOptions
                  ? this.props.presetOptions[input.bodyKey]
                  : []
              }
              selectedOption={this.props.presetFields && this.props.presetFields[input.bodyKey]}
              onSelect={option =>
                this.props.onInputPresetUpdate &&
                this.props.onInputPresetUpdate(input.bodyKey, option)
              }
            >
              {inputComponent}
            </InputPresetWrapper>
          </div>
          <style jsx>{`
            .objectContainer {
              padding: 4px;
              border: solid #cccccc;
              border-radius: 6px;
              margin: ${parentBodyKeys.length ? 8 : 32}px 0;
            }
            .container {
              margin: ${parentBodyKeys.length ? 8 : 32}px 0;
            }
            .textHelp {
              margin-top: ${8 / (parentBodyKeys.length + 1)}px;
              color: var(--color-RemoGray50);
            }
            .inputComponent {
              margin-top: ${12 / (parentBodyKeys.length + 1)}px;
            }
          `}</style>
        </div>
      )
    );
  }

  canSubmit() {
    if (this.state.loading) {
      return false;
    }

    const inputs = this.state.inputs;
    const metadata = this.state.metadata;

    const presetsValues: Record<string, any> = this.props.presetFields ?? {};
    for (const key of Object.keys(inputs)) {
      if (inputs[key] === undefined) {
        inputs[key] = presetsValues[key];
      }
    }

    if (!inputs) {
      throw new Error('No inputs found');
    }

    return _.every(this.props.params?.inputs, input => {
      if (input.optional) {
        return true;
      }
      if (!evaluateShouldShow(input.shouldShow, inputs, metadata)) {
        return true;
      }

      return inputs[input.bodyKey] !== undefined;
    });
  }

  setStateAsync<K extends keyof IDynamicCorpState>(state: Pick<IDynamicCorpState, K>) {
    return new Promise(resolve => {
      this.setState(state, resolve);
    });
  }

  readFileAsync(file: File): Promise<string | ArrayBuffer | null> {
    return new Promise(resolve => {
      const fr = new FileReader();
      fr.onload = () => {
        resolve(fr.result);
      };
      fr.readAsText(file);
    });
  }

  // eslint-disable-next-line complexity
  async submit() {
    if (this.props.pageName === undefined) {
      return;
    }

    await this.setStateAsync({
      loading: true,
      latestResponse: undefined,
    });

    const inputs = this.state.inputs;
    const body = await Bluebird.props(
      _.mapValues(inputs, (inputValue, key) => {
        const correspondingInput = _.find(
          this.props.params?.inputs,
          input => input.bodyKey === key,
        );

        if (correspondingInput?.type === DynamicCorpInputTypes.Project) {
          // todo do whatever project input expects
          return inputValue?.value;
        } else if (correspondingInput?.type === DynamicCorpInputTypes.SingleFileAttachment) {
          if (!_.isNil(inputValue)) {
            return this.readFileAsync(inputValue as File);
          }
          return undefined;
        }
        return inputValue;
      }),
    );
    /*
    XXX body.submitterEmail = this.props.email is a hack
    TODO: for commands that use body.submitterEmail, make an input
    {
      label: 'Email of requester',
      type: DynamicCorpInputTypes.String,
      default: DynamicCorpInputGlobals.USER_EMAIL,
      bodyKey: 'submitterEmail',
    },
    DynamicCorpInputGlobals (proposal): a list sentinel values
    - when used as values for `IDynamicCorpInput.default`, get replaced by a computed value (e.g. current corp user's email)
    - replacement happens at render-time
    */

    let currentResponse: IFormattedResponse;
    try {
      if (this.props.params?.corpJobName) {
        if (this.props.params?.mustRunAsAsyncCorpJob) {
          // TODO checkbox to allow opting into this
          const job = await invokeAsyncCorpJob(this.props.params.corpJobName, body as any);
          if (job.status === 'errored') {
            currentResponse = {
              message: job.errorReason || 'Internal server error',
              corpJobOrSfnId: job.id,
              success: false,
              longJob: true,
            };
          } else {
            currentResponse = {
              message: `Started long job ${job.id}`,
              success: true,
              corpJobOrSfnId: job.id,
              longJob: true,
            };
          }
        } else {
          const job = await invokeCorpJob(this.props.params.corpJobName, body as any);
          if (job.status === CorpJobStatus.ERRORED) {
            currentResponse = {
              message: job.errorReason || 'Internal server error',
              corpJobOrSfnId: job.id,
              success: false,
            };
          } else {
            const results: any | undefined = job.jsonOutput;
            currentResponse = {
              message: results?.message ?? SUCCESS_MESSAGE,
              success: true,
              componentProps: results?.componentProps,
              corpJobOrSfnId: job.id,
            };
          }
        }
      } else {
        // default to running as corp job
        const job = await invokeDynamicCorpJob(
          `${this.props.pageName}`,
          body,
          this.props.params?.mustRunAsAsyncCorpJob,
          this.props.params?.isPoll,
        );
        if (job.status === CorpJobStatus.ERRORED) {
          currentResponse = {
            message: job.errorReason || 'Internal server error',
            success: false,
          };
        } else if (this.props.params?.isPoll) {
          currentResponse = {
            message: `Polling server for ${job.id}`,
            corpJobOrSfnId: job.id,
            pending: true,
          };
        } else {
          const results = job.jsonOutput;
          currentResponse = {
            message: results?.message || SUCCESS_MESSAGE,
            success: true,
            componentProps: results?.componentProps,
            corpJobOrSfnId: results?.stepFunctionId || job.id,
          };
        }
      }
    } catch (err) {
      const errorResponse = err.response;

      const message =
        err?.status === 503
          ? TIMEOUT_ERROR_MESSAGE
          : errorResponse?.error || errorResponse?.message || 'Internal server error';

      currentResponse = {
        message,
      };
    }
    await this.setStateAsync({
      loading: !!currentResponse.pending,
      latestResponse: currentResponse,
    });
    if (currentResponse.pending) {
      const pollInterval = setInterval(async () => {
        const job = await apiFetch(null, `corp-api/jobs/job/${currentResponse.corpJobOrSfnId}`);
        if (![CorpJobStatus.ERRORED, CorpJobStatus.COMPLETED].includes(job.status)) {
          currentResponse.progress = job.progress;
          await this.setStateAsync({
            latestResponse: currentResponse,
          });
          return;
        }
        clearInterval(pollInterval);
        if (job.status === CorpJobStatus.ERRORED) {
          currentResponse = {
            message: job.errorReason || 'Internal server error',
            success: false,
          };
        } else {
          const results = job.jsonOutput;
          currentResponse = {
            message:
              results?.message ||
              'Your job was successfully started. Please visit the job lookup page for more details (including whether it completed successfully)',
            success: true,
            componentProps: results?.componentProps,
            corpJobOrSfnId: results?.stepFunctionId || job.id,
          };
        }
        await this.setStateAsync({
          loading: false,
          latestResponse: currentResponse,
        });
      }, FETCH_JOB_STATUS_INTERVAL_MS);
    }
  }

  renderNotFound() {
    return <DynamicCorpPageNotFound />;
  }

  renderOutput() {
    if (!this.state.latestResponse) {
      return null;
    }

    const { componentProps, corpJobOrSfnId, message, longJob } = this.state.latestResponse;

    if (
      this.props.pageName &&
      DynamicCorpOutputComponents[this.props.pageName] !== undefined &&
      componentProps
    ) {
      return DynamicCorpOutputComponents[this.props.pageName]!({
        ...componentProps,
        corpJobOrSfnId,
        longJob,
      });
    }

    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={message || ''} />
        {corpJobOrSfnId && (
          <>
            <p>
              {longJob
                ? 'This corp tool takes a long time to run. See the status of your corp job'
                : 'Job Lookup Page'}{' '}
              <Link href={`/lookup/${corpJobOrSfnId}`} target="_blank">
                here
              </Link>{' '}
            </p>
          </>
        )}
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  }

  // eslint-disable-next-line complexity
  render() {
    const disabled = this.props?.params?.disabled && !this.props?.forceRenderingDisabledTool;
    if (!this.props.isValid || !this.props.params || disabled) {
      return this.renderNotFound();
    }
    const vertical = !!this.props.onInputUpdate;
    const { success, pending } = this.state.latestResponse ?? {};

    const projectInput: IDynamicCorpInput | undefined = this.props.params.inputs.find(
      input => input.type === DynamicCorpInputTypes.Project,
    );
    const projectKey = projectInput?.bodyKey;
    const projectValue = projectKey ? this.getInput(projectKey, [])?.value : undefined;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: vertical ? '100%' : '480px auto',
          gridTemplateRows: vertical ? 'auto auto' : undefined,
          gridColumnGap: '48px',
          fontSize: '16px',
        }}
      >
        {!this.props.hideInformationPanel && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {!vertical && <H2>{this.props.params.pageTitle}</H2>}

            {this.props.params.description && (
              <div style={vertical ? undefined : { marginTop: '16px' }}>
                <SanitizedMarkdown text={this.props.params.description} />
                {!!this.props.params?.toolAuthor && (
                  <div className="mt-16">
                    <Text medium>Questions about this tool?</Text>
                    <div>Contact {this.props.params?.toolAuthor} ❤️</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div>
          <div className="form">
            {_.map(this.props.params.inputs, input => this.renderInput(input, [], projectValue))}
            {!vertical && (
              <>
                <div className="formSubmit">
                  <WithPlaySound
                    soundUrl={this.props.params.onCompletionSound}
                    probability={soundProbability}
                  >
                    <Button
                      type="primary"
                      onClick={() => {
                        this.submit();
                      }}
                      disabled={!this.canSubmit()}
                    >
                      {this.props.startButtonText
                        ? this.props.startButtonText
                        : this.props.params.mustRunAsAsyncCorpJob
                        ? 'Start long job'
                        : this.props.params.isPoll
                        ? 'Start poll job'
                        : 'Submit'}
                    </Button>
                  </WithPlaySound>
                </div>
              </>
            )}
          </div>

          {this.state.latestResponse?.corpJobOrSfnId && this.props.displayProgress && (
            <FetchCorpJobProgress
              jobId={this.state.latestResponse?.corpJobOrSfnId}
              intervalMS={FETCH_JOB_STATUS_INTERVAL_MS}
            />
          )}

          {!!this.state.latestResponse && (
            <div>
              <h3 style={success || pending ? {} : { color: 'red' }}>
                Latest Response ({success ? 'Successful' : pending ? 'Pending' : 'Failed'})
              </h3>
              {this.renderOutput()}
            </div>
          )}
        </div>
        <style jsx>{`
          .description {
            padding: 8px 16px;
            border-radius: var(--border-radius);
            background: var(--color-RemoGray01);
            color: var(--color-RemoGray50);
            word-wrap: break-word;
            overflow: auto;
          }
          .author {
            margin: 16px 0;
          }

          .form {
            margin-top: 16px;
          }
          .formCorpJobLabel {
            margin-bottom: 12px;
          }
          .formSubmit {
            margin-top: 32px;
          }
        `}</style>
        <style global jsx>{`
          .select-tasks-full {
            max-width: 800px;
          }

          .select-tasks-full .root .date-range-picker__date {
            width: 65px !important;
          }
        `}</style>
      </div>
    );
  }
}

export const useDynamicCorpParams = (name: string) => {
  // if it's in EnabledDynamicCorpParams, then no need to fetch anything
  const sharedParams =
    name in EnabledDynamicCorpParams ? EnabledDynamicCorpParams[name as DynamicCorpPage] : null;
  const asyncResult = useAsync(async () => {
    const dynamicCorpParams = await apiFetch(
      null,
      `corp-api/dynamic-corp/backend-defined/${name}`,
      {
        method: 'GET',
      },
    );
    return dynamicCorpParams;
  }, [name]);
  if (sharedParams)
    return {
      loading: false,
      value: sharedParams,
    };
  return asyncResult;
};

export const DynamicCorpPageNotFound = () => {
  const allPageTitles = useAllDynamicCorpPageTitles();
  return (
    <>
      <h3>The dynamic corp tool name you passed in is invalid</h3>
      {_.size(allPageTitles) ? (
        <>
          <p>Valid dynamic corp tools include</p>
          <ul>
            {_.map(allPageTitles, ({ name, pageTitle }) => (
              <li>
                <Link href={`/dct/${name}`}>{pageTitle}</Link>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p>Currently there are no dynamic corp tools</p>
      )}
    </>
  );
};

const DynamicCorpContainer: React.FC<{
  user: any;
  pageName?: DynamicCorpPage;
  hideInformationPanel?: boolean;
  displayProgress?: boolean;
  startButtonText?: string;
  queryParams?: Record<string, any>;
  presetFields?: Record<string, string>;
  presetOptions?: Record<string, string[]>;
  onInputUpdate?: (type: DynamicCorpInputTypes, key: string, value?: any) => void;
  onInputPresetUpdate?: (key: string, targetKey?: string) => void;
  forceRenderingDisabledTool?: boolean;
  embedded?: boolean /* embedded DCTs will not redirect to toolbox */;
}> = ({
  user,
  pageName,
  hideInformationPanel,
  displayProgress,
  startButtonText,
  queryParams = {},
  presetFields = {},
  presetOptions = {},
  onInputUpdate,
  onInputPresetUpdate,
  forceRenderingDisabledTool,
  embedded,
}) => {
  const history = useHistory();

  const query = useQueryParams();
  const [metadata, setMetadata] = useState({});

  const name = useMemo(() => {
    return pageName ?? (query.name as string);
  }, [query, pageName]);

  const needsDeprecationBanner = useMemo(() => {
    return !!name && DCT_DEPRECATION_BANNER_LIST.includes(name);
  }, [name]);

  const toolboxVersionExists = useMemo(() => {
    if (!name || query.noRedirect === 'true' || embedded) {
      return false;
    }

    const cleanName = _.camelCase(name);
    return ToolDefinitionByName[cleanName as ToolName];
  }, [name]);

  useEffect(() => {
    if (!toolboxVersionExists || !toolboxVersionExists?.name) {
      return;
    }

    const loadFeatureFlag = async () => {
      const enableRedirectToToolbox: boolean =
        !embedded &&
        (await unleashFeatureFlagEnabled({
          feature: 'enable-redirect-toolbox-from-dct',
        }));

      if (enableRedirectToToolbox && toolboxVersionExists.name) {
        history.push(`${GenAIOpsHubPaths.Toolbox}/${toolboxVersionExists.name}`);
      }
    };

    loadFeatureFlag();
  }, [toolboxVersionExists]);

  useEffect(() => {
    const loadFeatureFlag = async () => {
      const useYSCancellationFlow: boolean = await unleashFeatureFlagEnabled({
        feature: 'use-new-ys-cancellation-format-fe',
      });
      setMetadata(prevMetadata => ({
        ...prevMetadata,
        useYSCancellationFlow,
      }));
    };
    loadFeatureFlag();
  }, []);

  const localSharedParams = useMemo(() => {
    const filteredDynamicParams = forceRenderingDisabledTool
      ? AllDynamicCorpParams
      : EnabledDynamicCorpParams;
    return filteredDynamicParams[name as DynamicCorpPage];
  }, [name, forceRenderingDisabledTool]);

  const { data: fetchedSharedParams, isLoading: loadingSharedParams } =
    useQuery<IDynamicCorpParams>(
      `dynamic-corp-${name}`,
      () =>
        apiFetch(null, `corp-api/dynamic-corp/backend-defined/${name}`, {
          method: 'GET',
        }),
      {
        enabled: Boolean(name && !localSharedParams),
        refetchOnWindowFocus: false,
      },
    );

  const sharedParams = localSharedParams ?? fetchedSharedParams;

  const pageLocation = React.useMemo<string>(() => {
    if (history) return history.location.pathname;
    return window.location.pathname;
  }, [history]);

  const relevantQueryParams: Record<string, any> = useMemo(() => {
    if (Object.keys(queryParams).length > 0 && sharedParams) {
      const parameters: Record<string, any> = {};
      sharedParams.inputs.forEach(input => {
        if (input.type === DynamicCorpInputTypes.MultiUserOrTeamSelect) {
          const userIds: string[] = queryParams[`${input.bodyKey}_userIds`] ?? [];
          const workerTeamIds: string[] = queryParams[`${input.bodyKey}_workerTeamIds`] ?? [];
          if (userIds.length > 0 || workerTeamIds.length > 0) {
            parameters[input.bodyKey] = {
              userIds,
              workerTeamIds,
            };
          }
        } else {
          const param = queryParams[input.bodyKey];
          if (param !== undefined) {
            parameters[input.bodyKey] = convertParameterToDynamicInputValue(input.type, param);
          }
        }
      });
      return parameters;
    }
    return _.omit(query, 'name');
  }, [query, queryParams, sharedParams]);

  if (loadingSharedParams) {
    return <Spinner />;
  } else if (sharedParams) {
    const dynamicCorpPageComponent = (
      <DynamicCorp
        isValid
        hideInformationPanel={hideInformationPanel}
        displayProgress={displayProgress}
        startButtonText={startButtonText}
        params={sharedParams}
        email={user?.email}
        pageName={
          name as DynamicCorpPage /* XXX this is a lie for backend-defined corp jobs but should be safe to do */
        }
        pageLocation={pageLocation}
        documentTitle={sharedParams.pageTitle || 'Dynamic Corp'}
        queryParams={relevantQueryParams}
        presetFields={presetFields}
        presetOptions={presetOptions}
        onInputUpdate={onInputUpdate}
        onInputPresetUpdate={onInputPresetUpdate}
        forceRenderingDisabledTool={forceRenderingDisabledTool}
        presetMetadata={metadata}
      />
    );

    if (needsDeprecationBanner) {
      return <LayoutWithAlert>{dynamicCorpPageComponent}</LayoutWithAlert>;
    }

    return dynamicCorpPageComponent;
  } else {
    return <DynamicCorpPageNotFound />;
  }
};

export default DynamicCorpContainer;
